import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Settings, AlertTriangle, Users, Building2, Save, 
  RotateCcw, Check, Info, Sliders
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const LeaveConflictSettings = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [defaultThreshold, setDefaultThreshold] = useState(2);
  const [departmentThresholds, setDepartmentThresholds] = useState({});
  const [editingDept, setEditingDept] = useState(null);

  const fetchThresholds = async () => {
    try {
      setLoading(true);
      const response = await api.get('/leave-conflict-thresholds');
      setData(response.data);
      setDefaultThreshold(response.data.default_threshold || 2);
      
      // Build department thresholds map
      const thresholds = {};
      response.data.departments.forEach(dept => {
        thresholds[dept.department_id] = dept.conflict_threshold;
      });
      setDepartmentThresholds(thresholds);
    } catch (error) {
      toast.error('Failed to fetch threshold settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThresholds();
  }, []);

  const handleUpdateDefault = async () => {
    try {
      setSaving(true);
      await api.put('/leave-conflict-thresholds/default', {
        default_threshold: defaultThreshold
      });
      toast.success('Default threshold updated');
      fetchThresholds();
    } catch (error) {
      toast.error('Failed to update default threshold');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateDepartment = async (departmentId) => {
    try {
      setSaving(true);
      await api.put(`/leave-conflict-thresholds/${departmentId}`, {
        conflict_threshold: departmentThresholds[departmentId]
      });
      toast.success('Department threshold updated');
      setEditingDept(null);
      fetchThresholds();
    } catch (error) {
      toast.error('Failed to update department threshold');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDepartment = async (departmentId) => {
    try {
      setSaving(true);
      await api.delete(`/leave-conflict-thresholds/${departmentId}`);
      toast.success('Department reset to default');
      fetchThresholds();
    } catch (error) {
      toast.error('Failed to reset department threshold');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="conflict-settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Sliders className="text-indigo-600" />
            Leave Conflict Thresholds
          </h1>
          <p className="text-slate-600 mt-1">
            Configure when conflict warnings appear for team leaves
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="text-blue-600 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong> When the number of approved team leaves on the same day 
              reaches the threshold, managers will see a conflict warning in the Team Leave Calendar.
            </p>
            <p className="text-sm text-blue-600 mt-1">
              Example: If threshold is 2, a warning shows when 2+ team members have approved leave on the same day.
            </p>
          </div>
        </div>

        {/* Global Default */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="text-indigo-600" size={20} />
              Global Default Threshold
            </CardTitle>
            <CardDescription>
              This threshold applies to all departments unless overridden
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={defaultThreshold}
                  onChange={(e) => setDefaultThreshold(parseInt(e.target.value) || 1)}
                  className="w-24"
                />
                <span className="text-sm text-slate-600">or more team members on leave</span>
              </div>
              <Button 
                onClick={handleUpdateDefault}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Save size={16} className="mr-1" />
                Save Default
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Department Thresholds */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="text-indigo-600" size={20} />
              Department-Specific Thresholds
            </CardTitle>
            <CardDescription>
              Override the default threshold for specific departments based on team size
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium">Department</th>
                    <th className="pb-3 font-medium">Employees</th>
                    <th className="pb-3 font-medium">Threshold</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.departments?.map(dept => (
                    <tr key={dept.department_id} className="border-b hover:bg-gray-50">
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="text-slate-400" size={16} />
                          <span className="font-medium">{dept.department_name}</span>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1">
                          <Users className="text-slate-400" size={14} />
                          <span>{dept.employee_count}</span>
                        </div>
                      </td>
                      <td className="py-3">
                        {editingDept === dept.department_id ? (
                          <Input
                            type="number"
                            min="1"
                            max="20"
                            value={departmentThresholds[dept.department_id] || defaultThreshold}
                            onChange={(e) => setDepartmentThresholds(prev => ({
                              ...prev,
                              [dept.department_id]: parseInt(e.target.value) || 1
                            }))}
                            className="w-20"
                            autoFocus
                          />
                        ) : (
                          <span className="font-medium text-lg">
                            {dept.conflict_threshold}
                          </span>
                        )}
                      </td>
                      <td className="py-3">
                        {dept.is_custom ? (
                          <Badge className="bg-indigo-100 text-indigo-700">Custom</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">Default</Badge>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          {editingDept === dept.department_id ? (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleUpdateDepartment(dept.department_id)}
                                disabled={saving}
                                className="bg-emerald-600 hover:bg-emerald-700"
                              >
                                <Check size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingDept(null);
                                  setDepartmentThresholds(prev => ({
                                    ...prev,
                                    [dept.department_id]: dept.conflict_threshold
                                  }));
                                }}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingDept(dept.department_id)}
                              >
                                Edit
                              </Button>
                              {dept.is_custom && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-600 hover:bg-amber-50"
                                  onClick={() => handleResetDepartment(dept.department_id)}
                                  disabled={saving}
                                >
                                  <RotateCcw size={14} className="mr-1" />
                                  Reset
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Suggested Thresholds */}
            <div className="mt-6 bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={16} />
                Recommended Thresholds
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white p-3 rounded border">
                  <p className="font-medium text-slate-700">Small Teams (1-5)</p>
                  <p className="text-slate-500">Threshold: 2</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="font-medium text-slate-700">Medium Teams (6-15)</p>
                  <p className="text-slate-500">Threshold: 3-4</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="font-medium text-slate-700">Large Teams (15+)</p>
                  <p className="text-slate-500">Threshold: 5+</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default LeaveConflictSettings;
